// @ts-nocheck
import React from 'react';
import classNames from 'classnames';
import {
  LuScrollText,
  LuFileText,
  LuRefreshCw,
  LuLoader,
  LuChevronDown,
  LuCheckCircle2,
  LuXCircle,
  LuAlertTriangle,
  LuTag,
  LuDatabase,
  LuUpload,
  LuWifiOff,
  LuClock,
  LuActivity,
  LuHash,
  LuCalendarDays,
} from 'react-icons/lu';
import { FiUploadCloud, FiAlertCircle, FiDownload } from 'react-icons/fi';

import {
  SMART_COOLER_LOG_ACCENT,
  VIEW_SC_STYLES,
  VIEW_SC_STRINGS,
} from '@/constant/smartCooler/smartCooler.constant';
import { tid } from '@/utils/testId';
import { SMART_COOLER_TEST_IDS as TID } from '@/constant/testIds/smartCooler.testIds';
import {
  APP_BG,
  APP_BORDER,
  APP_TEXT,
  APP_SEMANTIC,
} from '@/constant/theme/colors.constant';
import { getDateTime, getDateTimeAgo } from '@/utils/dateTime';
import { Text } from '@/components/atoms/AppText';
import AppButton from '@/components/atoms/AppButton';
import { IconBadge } from '@/components/atoms/IconBadge';
import {
  IAppLog,
  ISmartCooler,
} from '@/pages/SmartCoolers/smartCooler.interface';
import {
  APP_LOG_STATUS_CODES,
  APP_LOG_STATUS_VALUES,
} from '@/constant/appLogs/appLogs.constant';
import {
  ExpandableCard,
  ExpandablePanel,
} from '@/components/elements/ExpandablePanel';
import { AppLogExpandedPanel } from '../AppLogExpandedPanel';

interface Props {
  item: ISmartCooler;
  logs: IAppLog[];
  logsLoading: boolean;
  logsTotalRecords: number;
  logCurrentPage: number;
  totalLogPages: number;
  isConnected: boolean;
  downloadInProgress: boolean;
  downloadingLogId: string | null;
  expandedLogId: string | null;
  onRefresh: () => void;
  onPageChange: (page: number) => void;
  onDownload: (e: React.MouseEvent, logId: string) => void;
  onExpandLog: (logId: string | null) => void;
  onOpenFetchModal: () => void;
}

// ── Per-status config ─────────────────────────────────────────────────────────
const STATUS_CFG = {
  uploading: {
    label: 'Uploading',
    Icon: FiUploadCloud,
    spin: true,
    accentBar: '#38bdf8', // sky-400
    iconBg: 'bg-sky-50 dark:bg-sky-950/40',
    iconText: 'text-sky-500 dark:text-sky-400',
    badge:
      'bg-sky-50 dark:bg-sky-950/50 border border-sky-200 dark:border-sky-700 text-sky-600 dark:text-sky-300',
    cardBorder: 'border-sky-200/70 dark:border-sky-800/50',
  },
  failed: {
    label: 'Failed',
    Icon: LuXCircle,
    spin: false,
    accentBar: '#f87171', // red-400
    iconBg: 'bg-red-50 dark:bg-red-950/40',
    iconText: 'text-red-500 dark:text-red-400',
    badge:
      'bg-red-50 dark:bg-red-950/50 border border-red-200 dark:border-red-700 text-red-600 dark:text-red-300',
    cardBorder: 'border-red-200/70 dark:border-red-800/50',
  },
  processed: {
    label: 'Processed',
    Icon: LuCheckCircle2,
    spin: false,
    accentBar: '#34d399', // emerald-400
    iconBg: 'bg-emerald-50 dark:bg-emerald-950/40',
    iconText: 'text-emerald-500 dark:text-emerald-400',
    badge:
      'bg-emerald-50 dark:bg-emerald-950/50 border border-emerald-200 dark:border-emerald-700 text-emerald-600 dark:text-emerald-300',
    cardBorder: 'border-emerald-200/70 dark:border-emerald-800/50',
  },
  default: {
    label: 'Pending',
    Icon: LuActivity,
    spin: false,
    accentBar: '#818cf8', // indigo-400
    iconBg: 'bg-indigo-50 dark:bg-indigo-950/40',
    iconText: 'text-indigo-500 dark:text-indigo-400',
    badge:
      'bg-indigo-50 dark:bg-indigo-950/50 border border-indigo-200 dark:border-indigo-700 text-indigo-600 dark:text-indigo-300',
    cardBorder: 'border-indigo-200/70 dark:border-indigo-800/50',
  },
} as const;

const getStatusCfg = (log: IAppLog) => {
  const isUp =
    log.statusCode === APP_LOG_STATUS_CODES.ACCEPTED ||
    log.status === APP_LOG_STATUS_VALUES.ACCEPTED;
  const isFail =
    (log.statusCode && log.statusCode >= APP_LOG_STATUS_CODES.FAILED) ||
    log.status === APP_LOG_STATUS_VALUES.FAILED;
  const isOk = log.status === APP_LOG_STATUS_VALUES.PROCESSED;
  if (isUp) return { ...STATUS_CFG.uploading, isUp, isFail, isOk };
  if (isFail) return { ...STATUS_CFG.failed, isUp, isFail, isOk };
  if (isOk) return { ...STATUS_CFG.processed, isUp, isFail, isOk };
  return { ...STATUS_CFG.default, isUp, isFail, isOk };
};

export const ViewSCDeviceLogsTab: React.FC<Props> = ({
  item,
  logs,
  logsLoading,
  logsTotalRecords,
  logCurrentPage,
  totalLogPages,
  isConnected,
  downloadInProgress,
  downloadingLogId,
  expandedLogId,
  onRefresh,
  onPageChange,
  onDownload,
  onExpandLog,
  onOpenFetchModal,
}) => {
  const renderDownloadAction = (
    log: IAppLog,
    cfg: ReturnType<typeof getStatusCfg>,
  ) => {
    const busy = downloadInProgress && log.logId === downloadingLogId;
    if (cfg.isUp)
      return (
        <span
          className={classNames(
            'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-semibold',
            cfg.badge,
          )}
        >
          <FiUploadCloud className="w-3 h-3 animate-bounce shrink-0" />
          <span>{VIEW_SC_STRINGS.LOG_ROW_UPLOADING}</span>
        </span>
      );
    if (cfg.isFail)
      return (
        <span
          className={classNames(
            'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-semibold',
            cfg.badge,
          )}
        >
          <FiAlertCircle className="w-3 h-3 shrink-0" />
          <span>{VIEW_SC_STRINGS.LOG_ROW_FAILED}</span>
        </span>
      );
    if (busy)
      return (
        <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-gray-400 dark:text-gray-500">
          <LuLoader className="w-3.5 h-3.5 animate-spin" />
          <span className="hidden sm:inline">Downloading…</span>
        </span>
      );
    return (
      <button
        data-testid={tid(TID.LOGS_DOWNLOAD_BTN(log.logId))}
        className={classNames(
          'inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[11px] font-semibold transition-all duration-150',
          'bg-indigo-50 dark:bg-indigo-950/50 hover:bg-indigo-100 dark:hover:bg-indigo-900/50',
          'text-indigo-600 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-700/60',
          'disabled:opacity-40 disabled:pointer-events-none',
        )}
        onClick={(e) => onDownload(e, log.logId)}
        disabled={!!downloadingLogId || !!downloadInProgress}
      >
        <FiDownload size={13} className="shrink-0" />
        <span>Download</span>
      </button>
    );
  };

  const toolbar = (
    <div
      className={`flex items-center justify-between gap-3 px-4 py-3 ${APP_BG.surface}`}
    >
      <div className="flex items-center gap-2.5">
        <IconBadge
          icon={<LuScrollText className="w-3.5 h-3.5" />}
          size="sm"
          variant="solid"
        />
        <div>
          <p
            className={`text-[13px] font-bold leading-none ${APP_TEXT.default}`}
          >
            {VIEW_SC_STRINGS.LOG_TOOLBAR_TITLE}
          </p>
          <p className={`text-[11px] mt-1 ${APP_TEXT.muted}`}>
            {logsLoading ? (
              <span className="inline-flex items-center gap-1">
                <LuLoader className="w-2.5 h-2.5 animate-spin" /> syncing…
              </span>
            ) : logsTotalRecords > 0 ? (
              <span>
                {logsTotalRecords}{' '}
                {logsTotalRecords === 1
                  ? VIEW_SC_STRINGS.LOG_FILE_SINGULAR
                  : VIEW_SC_STRINGS.LOG_FILE_PLURAL}
                {' · '}
                <span className="font-mono">{item.nativeId || '—'}</span>
              </span>
            ) : (
              <span className="font-mono">{item.nativeId || '—'}</span>
            )}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <AppButton
          data-testid={tid(TID.LOGS_REFRESH_BTN)}
          variant="neutral"
          size="sm"
          icon={
            <LuRefreshCw
              className={classNames('w-3 h-3', logsLoading && 'animate-spin')}
            />
          }
          onClick={onRefresh}
          disabled={logsLoading}
        >
          {VIEW_SC_STRINGS.LOG_REFRESH_BTN}
        </AppButton>
        {isConnected && (
          <AppButton
            data-testid={tid(TID.LOGS_FETCH_BTN)}
            variant="info"
            size="sm"
            icon={<LuUpload className="w-3 h-3" />}
            onClick={onOpenFetchModal}
          >
            {VIEW_SC_STRINGS.LOG_FETCH_NOW_BTN ?? 'Fetch Logs'}
          </AppButton>
        )}
      </div>
    </div>
  );

  const logSkeleton = () => (
    <div
      className={classNames(
        'flex overflow-hidden rounded-2xl border animate-pulse',
        APP_BG.base,
        APP_BORDER.soft,
      )}
    >
      <div className="w-[3px] shrink-0 bg-gray-200 dark:bg-gray-700/60" />
      <div className="flex items-center gap-3 px-4 py-4 flex-1">
        <div className="h-9 w-9 rounded-xl bg-gray-100 dark:bg-gray-800 shrink-0" />
        <div className="flex flex-col gap-2 flex-1">
          <div className="flex items-center gap-2">
            <div className="h-3 rounded-md bg-gray-200 dark:bg-gray-700 w-40" />
            <div className="h-4 w-16 rounded-full bg-gray-100 dark:bg-gray-800" />
          </div>
          <div className="flex items-center gap-2">
            <div className="h-2.5 rounded bg-gray-100 dark:bg-gray-800 w-24" />
            <div className="h-2.5 w-1 rounded bg-gray-100 dark:bg-gray-800 opacity-40" />
            <div className="h-2.5 rounded bg-gray-100 dark:bg-gray-800 w-20" />
          </div>
        </div>
        <div className="h-7 w-14 rounded-lg bg-gray-100 dark:bg-gray-800 shrink-0" />
      </div>
    </div>
  );

  return (
    <div className="flex flex-col gap-3">
      <ExpandablePanel
        items={logs}
        getItemId={(log) => log.logId}
        loading={logsLoading && logs.length === 0}
        skeletonCount={4}
        renderSkeleton={logSkeleton}
        emptyIcon={
          <LuFileText className="w-7 h-7 text-indigo-400 dark:text-indigo-400" />
        }
        emptyTitle={VIEW_SC_STRINGS.LOG_EMPTY_TITLE}
        emptyDescription={VIEW_SC_STRINGS.LOG_EMPTY_DESC}
        emptyAction={
          !isConnected ? (
            <span className="relative inline-flex items-center gap-1.5 rounded-full bg-red-50 dark:bg-red-950/50 border border-red-200 dark:border-red-800 px-3 py-1.5 text-[11px] font-semibold text-red-600 dark:text-red-300">
              <LuWifiOff className="w-3 h-3" />{' '}
              {VIEW_SC_STRINGS.LOG_OFFLINE_BANNER}
            </span>
          ) : (
            <AppButton
              data-testid={tid(TID.LOGS_EMPTY_FETCH_BTN)}
              variant="info"
              size="sm"
              icon={<LuUpload className="w-3.5 h-3.5" />}
              onClick={onOpenFetchModal}
            >
              {VIEW_SC_STRINGS.LOG_FETCH_NOW_BTN}
            </AppButton>
          )
        }
        toolbar={toolbar}
        expandedId={expandedLogId}
        onExpandChange={onExpandLog}
        renderItem={(log, isExp, onToggle) => {
          const cfg = getStatusCfg(log);
          const name = log.filename?.split('/').pop() ?? log.logId;
          const accentTier =
            SMART_COOLER_LOG_ACCENT[
              cfg.isUp
                ? 'uploading'
                : cfg.isFail
                  ? 'failed'
                  : cfg.isOk
                    ? 'processed'
                    : 'default'
            ];

          return (
            <ExpandableCard
              expanded={isExp}
              onToggle={onToggle}
              testId={tid(TID.LOGS_EXPAND_ROW(log.logId))}
              accentColor={cfg.accentBar}
              expandedRing={`ring-1 ${cfg.cardBorder.replace('border-', 'ring-')}`}
              showChevron={false}
              headerClassName="flex items-stretch gap-0 w-full text-left"
              header={
                <div className="flex items-center gap-3 px-3.5 py-3.5 flex-1 min-w-0">
                  {/* Status icon */}
                  <div
                    className={classNames(
                      'shrink-0 h-9 w-9 rounded-xl flex items-center justify-center',
                      cfg.iconBg,
                    )}
                  >
                    <cfg.Icon
                      className={classNames(
                        'w-4 h-4',
                        cfg.iconText,
                        cfg.spin && 'animate-spin',
                      )}
                    />
                  </div>

                  {/* Main text */}
                  <div className="flex-1 min-w-0 flex flex-col gap-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className={classNames(
                          'font-mono text-[12.5px] font-semibold truncate',
                          APP_TEXT.default,
                        )}
                      >
                        {name}
                      </span>
                      <span
                        className={classNames(
                          'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider',
                          cfg.badge,
                        )}
                      >
                        {log.status ?? cfg.label}
                      </span>
                      {log.statusCode != null && (
                        <span
                          className={classNames(
                            'hidden sm:inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[9px] font-mono',
                            APP_BG.muted,
                            APP_BORDER.soft,
                            APP_TEXT.faint,
                          )}
                        >
                          <LuHash className="w-2 h-2" />
                          {log.statusCode}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className={classNames(
                          'inline-flex items-center gap-1 text-[10px]',
                          APP_TEXT.muted,
                        )}
                      >
                        <LuCalendarDays className="w-2.5 h-2.5 shrink-0 text-gray-400" />
                        {getDateTime(log.createdOn)}
                      </span>
                      <span className="text-[10px] text-gray-200 dark:text-gray-700">
                        ·
                      </span>
                      <span
                        className={classNames(
                          'text-[10px] font-medium',
                          APP_TEXT.subtle,
                        )}
                      >
                        {getDateTimeAgo(log.createdOn)}
                      </span>
                      <span
                        className={classNames(
                          'hidden sm:inline-flex items-center gap-1 text-[9px] font-mono',
                          APP_TEXT.faint,
                        )}
                      >
                        <LuTag className="w-2 h-2 shrink-0" />
                        {log.logId}
                      </span>
                    </div>
                  </div>

                  {/* Download + chevron */}
                  <div
                    className="flex items-center gap-2 shrink-0 pl-1"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {renderDownloadAction(log, cfg)}
                  </div>
                  <div
                    className={classNames(
                      'shrink-0 h-6 w-6 rounded-md flex items-center justify-center transition-all duration-200',
                      isExp
                        ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-500 rotate-180'
                        : classNames(
                            APP_BG.muted,
                            APP_TEXT.subtle,
                            'group-hover:bg-gray-200 dark:group-hover:bg-gray-700',
                          ),
                    )}
                  >
                    <LuChevronDown className="w-3.5 h-3.5" />
                  </div>
                </div>
              }
            >
              {/* Expanded details */}
              <div
                className={classNames(
                  'flex items-center gap-2.5 px-4 py-2.5 border-b',
                  APP_BORDER.soft,
                )}
                style={{
                  background: `linear-gradient(to right, ${accentTier.from}09, transparent 60%)`,
                }}
              >
                <div
                  className="h-3.5 w-[3px] rounded-full shrink-0"
                  style={{
                    background: `linear-gradient(to bottom, ${accentTier.from}, ${accentTier.to})`,
                  }}
                />
                <span
                  className={classNames(
                    'text-[10px] font-bold uppercase tracking-widest',
                    APP_TEXT.subtle,
                  )}
                >
                  {VIEW_SC_STRINGS.LOG_ROW_DETAILS_LABEL}
                </span>
                <span
                  className={classNames(
                    'font-mono text-[9px] hidden sm:inline',
                    APP_TEXT.faint,
                  )}
                >
                  {log.logId}
                </span>
              </div>
              <div
                className={classNames(
                  'px-3 pb-4 pt-1 bg-white dark:bg-gray-900/40',
                )}
              >
                <AppLogExpandedPanel meta={log.meta} />
              </div>
            </ExpandableCard>
          );
        }}
      />

      {/* ════ PAGINATION ════ */}
      {totalLogPages > 1 && (
        <div
          className={classNames(
            'flex items-center justify-between gap-3 rounded-2xl border px-4 py-2.5',
            APP_BG.elevated,
            APP_BORDER.card,
          )}
        >
          <span className={classNames('text-xs tabular-nums', APP_TEXT.muted)}>
            <span className={classNames('font-semibold', APP_TEXT.soft)}>
              {logsTotalRecords}
            </span>{' '}
            logs · page{' '}
            <span className={classNames('font-semibold', APP_TEXT.soft)}>
              {logCurrentPage}
            </span>{' '}
            / {totalLogPages}
          </span>

          <div className="flex items-center gap-1.5">
            <AppButton
              data-testid={tid(TID.LOGS_PREV_BTN)}
              variant="neutral"
              size="sm"
              disabled={logCurrentPage <= 1 || logsLoading}
              onClick={() => onPageChange(logCurrentPage - 1)}
            >
              ← Prev
            </AppButton>

            {/* page number pills — desktop only */}
            <div className="hidden sm:flex items-center gap-1">
              {Array.from({ length: Math.min(totalLogPages, 5) }, (_, i) => {
                const page =
                  totalLogPages <= 5
                    ? i + 1
                    : logCurrentPage <= 3
                      ? i + 1
                      : logCurrentPage >= totalLogPages - 2
                        ? totalLogPages - 4 + i
                        : logCurrentPage - 2 + i;
                const isCur = page === logCurrentPage;
                return (
                  <button
                    key={page}
                    onClick={() => onPageChange(page)}
                    disabled={logsLoading}
                    className={classNames(
                      'h-6 w-6 rounded-md text-[11px] font-bold tabular-nums transition-colors',
                      isCur
                        ? 'bg-indigo-500 text-white shadow-sm'
                        : classNames(
                            APP_BG.muted,
                            APP_TEXT.subtle,
                            'hover:bg-gray-200 dark:hover:bg-gray-700',
                          ),
                    )}
                  >
                    {page}
                  </button>
                );
              })}
            </div>

            <AppButton
              data-testid={tid(TID.LOGS_NEXT_BTN)}
              variant="neutral"
              size="sm"
              disabled={logCurrentPage >= totalLogPages || logsLoading}
              onClick={() => onPageChange(logCurrentPage + 1)}
            >
              Next →
            </AppButton>
          </div>
        </div>
      )}
    </div>
  );
};
